import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import { Panel, PanelHeader, PanelTitle } from '@prisjakt/ui/panel';
import { ExpandableGrid } from 'src/components/expandable-grid';
import { useViewportContext } from 'src/components/viewport/context';
import CampaignLink from '../../areas/campaign/components/link';
import {
  trackCampaignLinkClick,
  trackCampaignItemClick,
} from '../../areas/campaign/tracking';
import ErrorBoundary from '../error-boundary';
import { ProductGridCard } from 'src/components/product-card-new';
import {
  trackInternalNavigationClick,
  InternalNavigationScope,
} from '../../globals/tracking/tracking';

const CampaignDeals = props => {
  const { t } = useContext(AppContext);
  const { deals, activeCampaign, campaignLinkQuery } = props;
  const { isTablet } = useViewportContext();
  if (!deals || deals.length === 0) {
    return null;
  }

  const campaignItem = (deal, index) => (
    <ProductGridCard
      key={deal.product.id}
      diffPercentage={deal.price.diffPercentage}
      category={
        deal.product.category && {
          name: deal.product.category.name,
        }
      }
      imageSrc={deal.product.media.first}
      link={deal.product.pathName}
      name={deal.product.name}
      lazyLoadImage
      price={
        deal.product.priceSummary.regular ||
        deal.product.priceSummary.alternative
      }
      rating={deal.product.userReviewSummary.rating}
      reviewsCount={deal.product.userReviewSummary.count}
      onClick={() => {
        trackCampaignItemClick(deal);
        trackInternalNavigationClick({
          scope: InternalNavigationScope.CAMPAIGN_PRODUCT_CARD,
          nextPage: deal.product.pathName,
          index,
        });
      }}
    />
  );

  return (
    <ErrorBoundary onError={() => null}>
      <Panel
        containerProps={{
          'data-test': 'Panel',
          'data-test-type': 'campaign-deals',
        }}
      >
        <PanelHeader>
          <PanelTitle>{activeCampaign.name}</PanelTitle>
          <CampaignLink
            queryString={campaignLinkQuery}
            linkContent={t('campaigns', 'link.view-all')}
            campaign={activeCampaign}
            onClick={() => {
              trackCampaignLinkClick(
                activeCampaign.id,
                'deals_component_header'
              );
              trackInternalNavigationClick({
                scope: InternalNavigationScope.CAMPAIGN_HEADER,
                nextPage: activeCampaign.web_uri.responsive,
              });
            }}
          />
        </PanelHeader>

        <ExpandableGrid
          limit={isTablet ? 3 : 4}
          expandText={t('common', 'button.show-more')}
          collapseText=""
          items={deals.map(campaignItem)}
          canCollapse={false}
          cssGrid
          expandedActions={
            <CampaignLink
              queryString={campaignLinkQuery}
              linkContent={t('campaigns', 'link.view-all')}
              campaign={activeCampaign}
              data-test="ShowAllCampaignsButton"
              onClick={() => {
                trackCampaignLinkClick(
                  activeCampaign.id,
                  'deals_component_show_all'
                );
                trackInternalNavigationClick({
                  scope: InternalNavigationScope.CAMPAIGN_SHOW_ALL,
                  nextPage: activeCampaign.web_uri.responsive,
                });
              }}
            />
          }
        />
      </Panel>
    </ErrorBoundary>
  );
};

const { string, shape, arrayOf, number, any } = PropTypes;

CampaignDeals.propTypes = {
  deals: any,
  campaignLinkQuery: string,
  activeCampaign: shape({
    id: number,
    name: string,
    type: string,
    state: string,
    slug: string,
    web_uri: shape({
      mobile: string,
      desktop: string,
      responsive: string,
    }),
    banners: arrayOf(
      shape({
        url: string,
        type: string,
        imageUrl: string,
      })
    ),
  }).isRequired,
};

CampaignDeals.defaultProps = {
  deals: undefined,
  campaignLinkQuery: null,
};

export default connect(state => ({
  activeCampaign: state.globals.campaigns.active.find(c => c.type === 'OFFER'),
}))(memo(CampaignDeals));
